import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";

const navItems = [
  { to: "/", label: "Acasă", testid: "nav-home" },
  { to: "/cine-suntem", label: "Cine suntem", testid: "nav-about" },
  { to: "/eveniment", label: "Evenimentul", testid: "nav-event" },
  { to: "/articole", label: "Articole", testid: "nav-articles" },
  { to: "/colaboratori", label: "Colaboratori", testid: "nav-collaborators" },
];

export const Layout = () => {
  const { pathname } = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    setMobileOpen(false);
  }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  return (
    <div className="App paper" data-testid="app-shell">
      <header className="border-b border-[color:var(--line)]/30 bg-[color:var(--ivory)]/90 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-12 py-4 sm:py-5 flex items-center justify-between gap-3">
          <Link to="/" className="group flex items-center gap-2 sm:gap-3 min-w-0 flex-shrink" data-testid="brand-link">
            <img
              src="/assets/logo-mark.png"
              alt="Sigla Asociației General George Pomuț"
              className="w-10 h-10 sm:w-11 sm:h-11 shrink-0 rounded-full ring-1 ring-[color:var(--gold)]/70 transition-transform duration-500 group-hover:scale-105"
              data-testid="brand-logo"
            />
            <div className="leading-tight min-w-0">
              <div className="font-display text-[14px] sm:text-[17px] tracking-tight text-[color:var(--ink)] truncate">Asociația General George Pomuț</div>
              <div className="eyebrow text-[9px] sm:text-[10px]">Oradea · România</div>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
            {navItems.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end
                data-testid={n.testid}
                className={({ isActive }) =>
                  `link-underline text-[13px] tracking-[0.16em] uppercase whitespace-nowrap ${isActive ? "text-[color:var(--gold)]" : "text-[color:var(--ink)]"}`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          {/* Mobile toggle */}
          <button
            type="button"
            className="md:hidden shrink-0 w-10 h-10 flex items-center justify-center text-[color:var(--ink)] hover:text-[color:var(--gold)] transition-colors"
            aria-label={mobileOpen ? "Închide meniul" : "Deschide meniul"}
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {/* Mobile menu panel */}
        {mobileOpen && (
          <div className="md:hidden border-t border-[color:var(--line)]/20 bg-[color:var(--ivory)]" data-testid="mobile-nav">
            <nav className="max-w-[1280px] mx-auto px-4 sm:px-6 py-4 flex flex-col">
              {navItems.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end
                  data-testid={`${n.testid}-mobile`}
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) =>
                    `py-4 border-b border-[color:var(--line)]/15 last:border-b-0 text-[14px] tracking-[0.18em] uppercase ${isActive ? "text-[color:var(--gold)]" : "text-[color:var(--ink)]"}`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
            </nav>
          </div>
        )}
      </header>

      <main data-testid="main-content">
        <Outlet />
      </main>

      <footer className="ink-bg mt-24" data-testid="site-footer">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-12 py-16 grid md:grid-cols-3 gap-10">
          <div>
            <div className="eyebrow text-[color:var(--gold-soft)]">Asociația</div>
            <h3 className="font-display text-3xl mt-3">General George Pomuț</h3>
            <p className="text-[color:var(--cream)]/70 mt-4 text-sm leading-relaxed max-w-xs">
              Onorăm memoria diplomatului român-american George Pomuț (1818–1882), unul dintre cei care au făurit America de astăzi.
            </p>
          </div>
          <div>
            <div className="eyebrow text-[color:var(--gold-soft)]">Contact</div>
            <ul className="mt-4 space-y-2 text-sm text-[color:var(--cream)]/80">
              <li>Oradea, județul Bihor</li>
              <li>România</li>
              <li className="pt-2 text-[color:var(--cream)]/60">Președinte: Dr. Aurel Mircea Romocia</li>
            </ul>
          </div>
          <div>
            <div className="eyebrow text-[color:var(--gold-soft)]">Navigare</div>
            <ul className="mt-4 space-y-2 text-sm">
              <li><Link to="/" className="text-[color:var(--cream)]/80 hover:text-[color:var(--gold-soft)]">Acasă</Link></li>
              <li><Link to="/cine-suntem" className="text-[color:var(--cream)]/80 hover:text-[color:var(--gold-soft)]">Cine suntem</Link></li>
              <li><Link to="/eveniment" className="text-[color:var(--cream)]/80 hover:text-[color:var(--gold-soft)]">Simpozion 30 mai</Link></li>
              <li><Link to="/articole" className="text-[color:var(--cream)]/80 hover:text-[color:var(--gold-soft)]">Articole</Link></li>
              <li><Link to="/colaboratori" className="text-[color:var(--cream)]/80 hover:text-[color:var(--gold-soft)]">Colaboratori</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10">
          <div className="max-w-[1280px] mx-auto px-6 lg:px-12 py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-[color:var(--cream)]/50">
            <span>© {new Date().getFullYear()} Asociația General George Pomuț. Toate drepturile rezervate.</span>
            <span className="tracking-[0.2em] uppercase">Memoria · Cultura · Diplomația</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
